import * as THREE from 'three';

import store from "../../../../../../../../store";
import {setSQFTActiveShapeId} from "../../../../../../../../actions/sqftMenuFunctions";
import {
  CABINET_BATHROOM_PART_IDS,
  CABINET_BOXES,
  CABINET_PART_IDS,
  SQFT_VIEW_MODE,
  WALL_MOUNTED_CABINET_PART_IDS,
} from "../../../../../../constants";
import {
  DEFAULT_BACKSPLASH_HEIGHT,
  DEFAULT_COUNTERTOP_HEIGHT,
} from "../../../../../../helpers/sqftCabinetsDataMapper";
import {getCurrentLayout} from "../../../../../../../appLayout";
import cabinetFeatureDragHelper from "../../../../../canvas/helpers/cabinetFeatureDragHelper";
import workspace from "../../../../../canvas/core/workspace";
import sqftShapeManager from "../../../../../canvas/store/sqftShapeManager";
import SqftStore from "../../../../../canvas/store/sqftStore";
import workspaceControllers from "../../../../../canvas/store/workspaceControllers";
import sqftShapeIntersectionsManager from "../../../../../canvas/store/sqftShapeIntersectionsManager";
import {CabinetsSet} from "../../../../../../helpers/sqftCabinetsDataMapper";
import {
  CABINET_PLACEMENT_TYPE,
  applyCabinetLineSnapPlacement,
  createCabinetPlacementContext,
  getShapeStartBounds,
  resolveCabinetPlacement,
} from "./cabinetPlacementResolver";
import {
  addCabinetShapeToWorkspace,
  getCurrentCabinetShapeFeature,
} from "../../../../../navigation/menu/cabinet/toolMenu/helpers/addCabinetShapeToWorkspace";
import PlacementPreviewPlate from "./placementPreviewPlate";
import {isEnabledColorForRenuitAndHomeDepot} from "../../../../../../helpers/sqft3dArHelper";
import {
  CABINET_FREE_DRAG_BOUNDARY_WARNING_DISTANCE,
  CabinetFreeDragBoundaryPreview,
  clampCabinetPositionToLayoutBounds,
  getCabinetFreeDragLayoutBounds,
  getMovedShapeBounds,
  isBoundsNearLayoutBoundary,
  isSameWorkspacePosition,
} from "./cabinetFreeDragBoundary";
import {renderErrorPopup} from "../../../../../../../../api/api-popups";
import {reportNonFatalError} from "../../../../../../../../misc/errorReporting/errorReporter";
import {loaderDisable} from "../../../../../../../ui/loaderView";

const PREVIEW_OPACITY = 0.45;
const LOADING_PREVIEW_COLOR = 0xa9cf21;
const LOADING_PREVIEW_OPACITY = 0.78;
const MIN_LOADING_DOT_RADIUS = 0.025;
const LOADING_PREVIEW_DELAY_CONFIG_KEY = 'sqftCabinetPanelDropPreviewDelay';

// Handles cabinet drops from the 2D sidebar into the 3D scene. During drag it creates
// a temporary workspace shape so the same snap/insert rules can drive both preview and final placement.
export default class CabinetPanelDropControls {
  constructor(threeScene, scene, camera, domElement) {
    this.threeScene = threeScene;
    this.scene = scene;
    this.camera = camera;
    this.domElement = domElement;
    this.raycaster = new THREE.Raycaster();
    this.pointer = new THREE.Vector2();
    this.dropPlane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
    this.planePoint = new THREE.Vector3();
    this.previewShape = null;
    this.previewShapePromise = null;
    this.previewPlacementContext = null;
    this.previewModel = null;
    this.previewModelData = null;
    this.previewModelLoadPromise = null;
    this.previewModelRequestId = 0;
    this.previewLifecycleId = 0;
    this.previewDragKey = null;
    this.usesDefaultDropPosition = false;
    this.pendingPreviewFrameId = null;
    this.pendingPreviewUpdate = null;
    this.targetGroupByShapeId = new Map();
    this.loadingPreview = null;
    this.loadingPreviewAnimationFrameId = null;
    this.placementPreview = new PlacementPreviewPlate(scene);
    this.freeDragBoundaryPreview = new CabinetFreeDragBoundaryPreview(scene);

    this.domElement.addEventListener('dragover', this._onDragOver, false);
    this.domElement.addEventListener('drop', this._onDrop, false);
    this.domElement.addEventListener('dragleave', this._onDragLeave, false);
  }

  dispose() {
    this.domElement.removeEventListener('dragover', this._onDragOver);
    this.domElement.removeEventListener('drop', this._onDrop);
    this.domElement.removeEventListener('dragleave', this._onDragLeave);
    this._clearPreview();
    this.targetGroupByShapeId.clear();
    this.placementPreview.dispose();
    this.freeDragBoundaryPreview.dispose();
  }

  _disposePlacementPreview() {
    this.placementPreview.clear();
  }

  _disposeFreeDragBoundaryPreview() {
    this.freeDragBoundaryPreview.clear();
  }

  _getFreeDropPosition(shape, rawPosition) {
    if (!shape || !rawPosition) {
      return {
        position: rawPosition,
        bounds: null,
        layoutBounds: null,
      };
    }

    const startShapeBounds = getShapeStartBounds(shape);
    const layoutBounds = getCabinetFreeDragLayoutBounds(shape);

    return {
      position: clampCabinetPositionToLayoutBounds(rawPosition, rawPosition, startShapeBounds, layoutBounds),
      bounds: startShapeBounds,
      layoutBounds,
    };
  }

  _updateFreeDropBoundaryPreview(shape, rawPosition, clampedPosition, placement, startShapeBounds, layoutBounds) {
    if (placement || !shape || !rawPosition || !clampedPosition || !startShapeBounds || !layoutBounds) {
      this._disposeFreeDragBoundaryPreview();
      return;
    }

    const clampedBounds = getMovedShapeBounds(clampedPosition, rawPosition, startShapeBounds);
    const shouldShow = !isSameWorkspacePosition(rawPosition, clampedPosition)
      || isBoundsNearLayoutBoundary(
        clampedBounds,
        layoutBounds,
        CABINET_FREE_DRAG_BOUNDARY_WARNING_DISTANCE
      );

    this.freeDragBoundaryPreview.update({
      bounds: layoutBounds,
      modelScale: this.threeScene.modelScale || 1,
      shouldShow,
    });
  }

  // Sidebar drags are the only source for a new cabinet before it exists in the workspace.
  // Desktop and mobile paths store that payload differently, so normalize both here.
  _getDragData(event) {
    const activeDragData = cabinetFeatureDragHelper.getActiveDraggedFeatureData();

    if (activeDragData) {
      return activeDragData;
    }

    const dataTransfer = event.dataTransfer;
    const featureId = dataTransfer && dataTransfer.getData('featureId');
    const parentFeatureId = dataTransfer && dataTransfer.getData('parentFeatureId');
    const partId = dataTransfer && dataTransfer.getData('partId');

    if (!featureId || !parentFeatureId || !partId) {
      return null;
    }

    return {
      featureId: parseInt(featureId, 10),
      parentFeatureId: parseInt(parentFeatureId, 10),
      partId,
      width: Number(dataTransfer.getData('width')),
      height: Number(dataTransfer.getData('height')),
      depth: Number(dataTransfer.getData('depth')),
    };
  }

  // The 3D scene accepts only cabinet catalog items that can be converted into
  // a workspace shape with a concrete feature and parent feature.
  _canDropCabinet(dragData) {
    return dragData
      && (
        CABINET_BOXES.includes(dragData.partId)
        || CABINET_PART_IDS.includes(dragData.partId)
        || CABINET_BATHROOM_PART_IDS.includes(dragData.partId)
      )
      && dragData.featureId
      && dragData.parentFeatureId;
  }

  _getFeaturesFromDragData(dragData) {
    const layout = getCurrentLayout();
    const feature = layout.getFeatureById(dragData.featureId, true);
    const parentFeature = layout.getFeatureById(dragData.parentFeatureId, true);

    return {
      feature: getCurrentCabinetShapeFeature(feature, parentFeature),
      parentFeature,
    };
  }

  _canPrepareCabinetFromDragData(dragData) {
    if (!this._canDropCabinet(dragData)) {
      return false;
    }

    const {feature, parentFeature} = this._getFeaturesFromDragData(dragData);

    return !!feature
      && !!parentFeature
      && isEnabledColorForRenuitAndHomeDepot(parentFeature.dbFeature.id);
  }

  _setRayFromEvent(event) {
    const rect = this.domElement.getBoundingClientRect();
    this.pointer.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    this.pointer.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
    this.raycaster.setFromCamera(this.pointer, this.camera);
  }

  _isUpperCabinetDrag(dragData) {
    return WALL_MOUNTED_CABINET_PART_IDS.includes(dragData.partId);
  }

  _getCursorPlaneY(dragData) {
    if (!this._isUpperCabinetDrag(dragData)) {
      return 0;
    }

    const modelScale = this.threeScene.modelScale || 1;
    const height = Number(dragData.height) || 0;

    // Upper cabinets are mounted above the countertop, so projecting onto the floor makes
    // their 3D model appear away from the cursor in perspective view.
    return (DEFAULT_COUNTERTOP_HEIGHT + DEFAULT_BACKSPLASH_HEIGHT + (height / 2)) * modelScale;
  }

  _hasPlacedCabinetShapes() {
    return workspace.getShapes().some(shape => (
      shape
      && (!this.previewShape || shape.id !== this.previewShape.id)
      && (
        CABINET_BOXES.includes(shape.partId)
        || CABINET_PART_IDS.includes(shape.partId)
        || CABINET_BATHROOM_PART_IDS.includes(shape.partId)
      )
    ));
  }

  // The business position is still the 2D workspace top-left point. The 3D
  // pointer is projected onto the correct cabinet lane, then converted back to
  // workspace units so normal cabinet placement rules can run.
  _getDropPosition(event, dragData) {
    if (this.usesDefaultDropPosition) {
      return null;
    }

    if (!this._hasPlacedCabinetShapes()) {
      this.usesDefaultDropPosition = true;
      return null;
    }

    this._setRayFromEvent(event);
    this.dropPlane.constant = -this._getCursorPlaneY(dragData);

    if (!this.raycaster.ray.intersectPlane(this.dropPlane, this.planePoint)) {
      return null;
    }

    const modelScale = this.threeScene.modelScale || 1;
    const width = Number(dragData.width) || 0;
    const depth = Number(dragData.depth) || 0;

    return {
      x: (this.planePoint.x / modelScale) - (width / 2),
      y: (this.planePoint.z / modelScale) - (depth / 2),
    };
  }

  // The loading preview is shown while GLB preview data is being prepared.
  _disposeLoadingPreview() {
    if (this.loadingPreviewAnimationFrameId) {
      window.cancelAnimationFrame(this.loadingPreviewAnimationFrameId);
      this.loadingPreviewAnimationFrameId = null;
    }

    if (!this.loadingPreview) {
      return;
    }

    this.loadingPreview.traverse((child) => {
      if (child.geometry) {
        child.geometry.dispose();
      }

      if (child.material) {
        child.material.dispose();
      }
    });

    this.scene.remove(this.loadingPreview);
    this.loadingPreview = null;
  }

  _animateLoadingPreview = () => {
    if (!this.loadingPreview) {
      this.loadingPreviewAnimationFrameId = null;
      return;
    }

    const dots = this.loadingPreview.userData.dots || [];
    const time = performance.now() * 0.006;

    dots.forEach((dot, index) => {
      const opacity = 0.22 + (Math.sin(time - (index * 0.7)) + 1) * 0.35;
      dot.material.opacity = opacity;
    });

    this.loadingPreviewAnimationFrameId = window.requestAnimationFrame(this._animateLoadingPreview);
  }

  _createLoadingPreview(dragData) {
    const modelScale = this.threeScene.modelScale || 1;
    const width = (Number(dragData.width) || 1) * modelScale;
    const depth = (Number(dragData.depth) || 1) * modelScale;
    const dotRadius = Math.max(Math.min(width, depth) * 0.035, MIN_LOADING_DOT_RADIUS);
    const spinnerRadius = Math.max(Math.min(width, depth) * 0.22, dotRadius * 3);
    const group = new THREE.Group();
    const outlineGeometry = new THREE.BufferGeometry().setFromPoints([
      new THREE.Vector3(-width / 2, 0, -depth / 2),
      new THREE.Vector3(width / 2, 0, -depth / 2),
      new THREE.Vector3(width / 2, 0, depth / 2),
      new THREE.Vector3(-width / 2, 0, depth / 2),
      new THREE.Vector3(-width / 2, 0, -depth / 2),
    ]);
    const outlineMaterial = new THREE.LineBasicMaterial({
      color: LOADING_PREVIEW_COLOR,
      transparent: true,
      opacity: LOADING_PREVIEW_OPACITY,
      depthTest: false,
    });
    const outline = new THREE.Line(outlineGeometry, outlineMaterial);
    const dots = Array.from({ length: 8 }).map((_, index) => {
      const angle = (index * Math.PI * 2) / 8;
      const dotGeometry = new THREE.CircleGeometry(dotRadius, 20);
      const dotMaterial = new THREE.MeshBasicMaterial({
        color: LOADING_PREVIEW_COLOR,
        transparent: true,
        opacity: LOADING_PREVIEW_OPACITY,
        side: THREE.DoubleSide,
        depthTest: false,
      });
      const dot = new THREE.Mesh(dotGeometry, dotMaterial);

      dot.rotation.x = -Math.PI / 2;
      dot.position.set(Math.cos(angle) * spinnerRadius, modelScale * 0.035, Math.sin(angle) * spinnerRadius);
      group.add(dot);

      return dot;
    });

    group.add(outline);
    group.renderOrder = 996;
    group.userData = {
      width,
      depth,
      dots,
    };

    this.scene.add(group);
    this.loadingPreview = group;

    if (!this.loadingPreviewAnimationFrameId) {
      this.loadingPreviewAnimationFrameId = window.requestAnimationFrame(this._animateLoadingPreview);
    }
  }

  _updateLoadingPreview(dragData, position) {
    if (!position || this.previewModel) {
      return;
    }

    const modelScale = this.threeScene.modelScale || 1;
    const width = (Number(dragData.width) || 1) * modelScale;
    const depth = (Number(dragData.depth) || 1) * modelScale;

    if (
      this.loadingPreview
      && (this.loadingPreview.userData.width !== width || this.loadingPreview.userData.depth !== depth)
    ) {
      this._disposeLoadingPreview();
    }

    if (!this.loadingPreview) {
      this._createLoadingPreview(dragData);
    }

    this.loadingPreview.position.set(
      (position.x + ((Number(dragData.width) || 0) / 2)) * modelScale,
      this._getCursorPlaneY(dragData),
      (position.y + ((Number(dragData.depth) || 0) / 2)) * modelScale
    );
  }

  // Shows where the new cabinet would attach. Green means a normal snap to an
  // available edge; red means an insert that will make room in an existing run.
  _updatePlacementPreview(placement, dragData) {
    if (!placement || !placement.hoveredLine) {
      this._disposePlacementPreview();
      return;
    }

    const modelScale = this.threeScene.modelScale || 1;
    const targetGroup = this._findGroupByShapeId(placement.targetShape.id);

    this.placementPreview.update({
      placement,
      modelScale,
      targetObject: targetGroup,
      fallbackObject: this.previewModel,
      fallbackBounds: this._getPreviewFallbackBounds(dragData),
    });
  }

  _findGroupByShapeId(shapeId) {
    if (this.targetGroupByShapeId.has(shapeId)) {
      const cachedGroup = this.targetGroupByShapeId.get(shapeId);

      if (cachedGroup && cachedGroup.parent) {
        return cachedGroup;
      }

      this.targetGroupByShapeId.delete(shapeId);
    }

    let result = null;

    this.scene.traverse((object) => {
      if (result || !object.userData || object.userData.shapeId !== shapeId) {
        return;
      }

      result = object;
    });

    if (result) {
      this.targetGroupByShapeId.set(shapeId, result);
    }

    return result;
  }

  _getPreviewFallbackBounds(dragData) {
    const modelScale = this.threeScene.modelScale || 1;
    const height = Math.max(Number(dragData.height) || 0, 1) * modelScale;
    const baseY = this._isUpperCabinetDrag(dragData)
      ? (DEFAULT_COUNTERTOP_HEIGHT + DEFAULT_BACKSPLASH_HEIGHT) * modelScale
      : 0;

    return {
      height,
      centerY: baseY + (height / 2),
    };
  }

  // Preview must use the same resolver as final drop, otherwise the user can
  // see one target while the committed cabinet lands in another place.
  _resolvePreviewPlacement(shape, position, dragData) {
    if (!shape || !position) {
      this._updatePlacementPreview(null, dragData);
      return null;
    }

    if (!this.previewPlacementContext) {
      this.previewPlacementContext = createCabinetPlacementContext(shape);
    }

    const placement = resolveCabinetPlacement(
      shape,
      position,
      position,
      getShapeStartBounds(shape),
      this.previewPlacementContext
    );
    this._updatePlacementPreview(placement, dragData);

    return placement;
  }

  _getDebugPreviewLoadingDelay() {
    const rawDelay = document.kvConfig && document.kvConfig[LOADING_PREVIEW_DELAY_CONFIG_KEY];
    const delay = parseInt(rawDelay, 10);

    return Number.isFinite(delay) && delay > 0 ? delay : 0;
  }

  _waitForDebugPreviewLoadingDelay() {
    const delay = this._getDebugPreviewLoadingDelay();

    if (!delay) {
      return Promise.resolve();
    }

    return new Promise(resolve => window.setTimeout(resolve, delay));
  }

  _disposePreviewModel() {
    const cabinetLoader = this._getCabinetLoader();

    if (this.previewModel) {
      this.previewModel.traverse((child) => {
        if (!child.isMesh || !child.material) {
          return;
        }

        const materials = Array.isArray(child.material) ? child.material : [child.material];
        materials.forEach((material) => {
          if (material.userData && material.userData.isCabinetPreviewMaterial) {
            material.dispose();
          }
        });
      });
    }

    if (this.previewModel) {
      this.scene.remove(this.previewModel);
    }

    if (this.previewModelData && cabinetLoader) {
      cabinetLoader.instances.delete(cabinetLoader._makeKey(this.previewModelData));
    }

    this.previewModel = null;
    this.previewModelData = null;
    this.previewModelLoadPromise = null;
  }

  // Clears all temporary 3D preview objects and optionally keeps the workspace shape for final commit.
  _clearPreview({ keepShape = false } = {}) {
    this.previewLifecycleId += 1;
    this.previewModelRequestId += 1;
    this._cancelPendingPreviewUpdate();
    this._disposePreviewModel();
    this._disposeLoadingPreview();
    this._disposePlacementPreview();
    this._disposeFreeDragBoundaryPreview();

    if (!keepShape && this.previewShape) {
      sqftShapeManager.removeShapeById(this.previewShape.id);
    }

    this.previewShape = null;
    this.previewShapePromise = null;
    this.previewPlacementContext = null;
    this.previewDragKey = null;
    this.usesDefaultDropPosition = false;
  }

  _cancelPendingPreviewUpdate() {
    if (this.pendingPreviewFrameId) {
      window.cancelAnimationFrame(this.pendingPreviewFrameId);
      this.pendingPreviewFrameId = null;
    }

    this.pendingPreviewUpdate = null;
  }

  _clearStalePreviewModel(model, modelData) {
    if (!model) {
      return;
    }

    this.scene.remove(model);

    const cabinetLoader = this._getCabinetLoader();
    if (modelData && cabinetLoader) {
      cabinetLoader.instances.delete(cabinetLoader._makeKey(modelData));
    }
  }

  _getCabinetLoader() {
    return this.threeScene
      && this.threeScene.modelsLoader
      && this.threeScene.modelsLoader.loaders
      && this.threeScene.modelsLoader.loaders.cabinets
      && this.threeScene.modelsLoader.loaders.cabinets.loader;
  }

  _makeModelTransparent(model) {
    model.traverse((child) => {
      if (!child.isMesh || !child.material) {
        return;
      }

      const sourceMaterials = Array.isArray(child.material) ? child.material : [child.material];
      const transparentMaterials = sourceMaterials.map((material) => {
        const clonedMaterial = material.clone();
        clonedMaterial.transparent = true;
        clonedMaterial.opacity = PREVIEW_OPACITY;
        clonedMaterial.depthWrite = false;
        clonedMaterial.userData = {
          ...clonedMaterial.userData,
          isCabinetPreviewMaterial: true,
        };
        clonedMaterial.needsUpdate = true;
        return clonedMaterial;
      });

      child.material = Array.isArray(child.material) ? transparentMaterials : transparentMaterials[0];
    });
  }

  async _getCabinetPositionData(shape) {
    const positions = await CabinetsSet.prepareCabinetsPositions(
      store.getState().sqftState.SQFTViewMode === SQFT_VIEW_MODE.threeDModeFramed
    );

    return positions.find(position => position.shapeId === shape.id) || null;
  }

  // After the GLB has been built once, drag movement only changes the preview
  // cabinet position. Reusing the loaded model avoids recalculating all cabinet
  // positions on every dragover frame.
  _movePreviewModelToShape(shape) {
    if (!this.previewModel || !shape || typeof shape.getOriginPointOnWorkspace !== 'function') {
      return;
    }

    const modelScale = this.threeScene.modelScale || 1;
    const originPointOnWorkspace = shape.getOriginPointOnWorkspace();

    this.previewModel.position.x = originPointOnWorkspace.x * modelScale;
    this.previewModel.position.z = originPointOnWorkspace.y * modelScale;
  }

  // Builds the translucent 3D preview model for the temporary workspace shape.
  // The preview is marked so the cabinet loader skips full post-processing that
  // only matters after a real drop.
  async _loadPreviewModel(shape, cabinetLoader, requestId) {
    const positionData = await this._getCabinetPositionData(shape);

    if (requestId !== this.previewModelRequestId || !positionData) {
      return;
    }

    const previewPositionData = {
      ...positionData,
      isCabinetDragPreview: true,
    };

    this.previewModelData = previewPositionData;
    await this._waitForDebugPreviewLoadingDelay();

    if (requestId !== this.previewModelRequestId) {
      return;
    }

    this.previewModel = await cabinetLoader.load(previewPositionData);

    if (requestId !== this.previewModelRequestId || !this.previewModel) {
      this._clearStalePreviewModel(this.previewModel, previewPositionData);
      this.previewModel = null;
      this.previewModelData = null;
      return;
    }

    this._makeModelTransparent(this.previewModel);
    this.previewModel.renderOrder = 997;
    this._movePreviewModelToShape(shape);
    this._disposeLoadingPreview();
  }

  // Creates the temporary workspace shape that represents "the cabinet being
  // dragged from the sidebar". This lets existing 2D snap/insert logic evaluate
  // the new cabinet before the user commits the drop.
  async _ensurePreviewShape(dragData, position) {
    if (this.previewShape) {
      if (position) {
        this.previewShape.setPosition(position);
      }

      return this.previewShape;
    }

    if (this.previewShapePromise) {
      const previewShape = await this.previewShapePromise;

      if (previewShape && position) {
        previewShape.setPosition(position);
      }

      return previewShape;
    }

    const {feature, parentFeature} = this._getFeaturesFromDragData(dragData);

    if (!feature || !parentFeature) {
      return null;
    }

    const lifecycleId = this.previewLifecycleId;
    const previewShapePromise = addCabinetShapeToWorkspace(feature, parentFeature, {
      position,
      shouldResolveParentFeature: false,
      shouldReloadIn3D: false,
      shouldActivateShape: false,
      shouldUpdateCalculationsOnCreate: false,
    });
    this.previewShapePromise = previewShapePromise;
    const previewShape = await previewShapePromise;

    if (lifecycleId !== this.previewLifecycleId) {
      if (previewShape) {
        sqftShapeManager.removeShapeById(previewShape.id);
      }

      return null;
    }

    this.previewShape = previewShape;
    this.previewShapePromise = null;

    return this.previewShape;
  }

  // Keeps the 3D preview in sync with the temporary workspace shape. Model load
  // is single-flight per drag; subsequent frames just move the existing model.
  async _updatePreviewModel(shape) {
    const cabinetLoader = this._getCabinetLoader();

    if (!cabinetLoader || !shape) {
      return;
    }

    if (this.previewModel) {
      this._movePreviewModelToShape(shape);
      return;
    }

    if (!this.previewModelLoadPromise) {
      const requestId = ++this.previewModelRequestId;

      this.previewModelLoadPromise = this._loadPreviewModel(shape, cabinetLoader, requestId)
        .finally(() => {
          if (requestId === this.previewModelRequestId) {
            this.previewModelLoadPromise = null;
          }
        });
    }

    await this.previewModelLoadPromise;
    this._movePreviewModelToShape(shape);
  }

  // Applies the selected placement behavior to the real workspace shape. Insert
  // delegates to auto-shuffle because neighboring cabinets may need to move;
  // snap only aligns the new cabinet to the chosen edge.
  _applyPlacementToShape(shape, placement) {
    if (!placement) {
      return;
    }

    if (
      placement.type === CABINET_PLACEMENT_TYPE.INSERT
      && shape.shapeAutoShuffle
      && typeof shape.shapeAutoShuffle.performFullAutoShuffle === 'function'
    ) {
      shape.shapeAutoShuffle.performFullAutoShuffle(
        placement.insertPoint,
        placement.hoveredLine,
        placement.targetShape
      );
      return;
    }

    if (
      placement.edge
      && (
        placement.edge.role === 'back-wall'
        || placement.edge.role === 'side-wall'
      )
    ) {
      applyCabinetLineSnapPlacement(shape, placement);
      return;
    }

    if (
      placement.type === CABINET_PLACEMENT_TYPE.SNAP
      && shape.shapeAutoShuffle
      && typeof shape.shapeAutoShuffle.alignShapeToExistingShapes === 'function'
    ) {
      shape.shapeAutoShuffle.alignShapeToExistingShapes(
        placement.position,
        placement.hoveredLine,
        placement.targetShape
      );
    }
  }

  async _reloadModelType(typeKey) {
    const modelsLoader = this.threeScene && this.threeScene.modelsLoader;

    if (!modelsLoader) {
      return;
    }

    await modelsLoader.reloadAllModelsOfType(typeKey, () => true);
  }

  async _reloadCabinets() {
    await this._reloadModelType('cabinets');
  }

  _hasCabinetLoadFailed(shape) {
    const modelsLoader = this.threeScene && this.threeScene.modelsLoader;

    return !!(
      modelsLoader
      && shape
      && typeof modelsLoader.hasFailedCabinetLoadForShape === 'function'
      && modelsLoader.hasFailedCabinetLoadForShape(shape.id)
    );
  }

  _showUnavailableCabinetModelPopup() {
    loaderDisable();
    renderErrorPopup('messageBox.cabinet3dModelUnavailable');
  }

  _removeFailedDroppedShape(shape) {
    if (!shape) {
      return;
    }

    sqftShapeManager.removeShapeById(shape.id);
    sqftShapeIntersectionsManager.updateShapesIntersections();
    workspaceControllers.snappedShapeGroups.updateShapesSnappedGroups(false);
    SqftStore.updateCalculations();
  }

  // New cabinet placement can change generated countertop geometry for the whole run.
  async _reloadCountertops() {
    await this._reloadModelType('countertops');
  }

  // One preview tick updates all drag-facing state: temporary workspace shape,
  // snap/insert candidate, loading outline, and translucent 3D model.
  _updateDragPreview(dragData, position) {
    this._updateLoadingPreview(dragData, position);

    this._ensurePreviewShape(dragData, position)
      .then((shape) => {
        const placement = this._resolvePreviewPlacement(shape, position, dragData);
        const freeDropPosition = this._getFreeDropPosition(shape, position);
        const previewPosition = placement && placement.position ? placement.position : freeDropPosition.position;

        if (shape && previewPosition) {
          shape.setPosition(previewPosition);
          this._updateLoadingPreview(dragData, previewPosition);
        }

        this._updateFreeDropBoundaryPreview(
          shape,
          position,
          freeDropPosition.position,
          placement,
          freeDropPosition.bounds,
          freeDropPosition.layoutBounds
        );

        return this._updatePreviewModel(shape);
      })
      .catch((error) => {
        console.error('[CabinetPanelDropControls] Could not update cabinet drag preview:', error);
      });
  }

  // Native HTML dragover can fire faster than rendering. Keep only the latest
  // pointer position per frame so overlap-heavy placement checks do not pile up.
  _queueDragPreviewUpdate(dragData, position) {
    this.pendingPreviewUpdate = {
      dragData,
      position,
    };

    if (this.pendingPreviewFrameId) {
      return;
    }

    this.pendingPreviewFrameId = window.requestAnimationFrame(() => {
      const update = this.pendingPreviewUpdate;

      this.pendingPreviewFrameId = null;
      this.pendingPreviewUpdate = null;

      if (!update) {
        return;
      }

      this._updateDragPreview(update.dragData, update.position);
    });
  }

  // Handles the in-progress sidebar drag without committing product state.
  // The actual cabinet is still temporary until drop.
  _onDragOver = (event) => {
    const dragData = this._getDragData(event);

    if (!this._canPrepareCabinetFromDragData(dragData)) {
      this._clearPreview();
      return;
    }

    event.preventDefault();
    event.dataTransfer.dropEffect = 'copy';
    const position = this._getDropPosition(event, dragData);
    const dragKey = `${dragData.featureId}:${dragData.parentFeatureId}:${dragData.partId}`;

    if (this.previewDragKey && this.previewDragKey !== dragKey) {
      this._clearPreview();
    }

    this.previewDragKey = dragKey;

    this._queueDragPreviewUpdate(dragData, position);
  }

  // Converts the temporary preview shape into a real cabinet in the workspace.
  // Only lower cabinet drops can change generated countertop geometry.
  _onDrop = (event) => {
    const dragData = this._getDragData(event);

    if (!this._canPrepareCabinetFromDragData(dragData)) {
      this._clearPreview();
      return;
    }

    event.preventDefault();

    const position = this._getDropPosition(event, dragData);
    if (!position && !this.usesDefaultDropPosition) {
      this._clearPreview();
      return;
    }

    Promise.resolve(this.previewShape || this.previewShapePromise || this._ensurePreviewShape(dragData, position)).then(async (shape) => {
      if (!shape) {
        this._clearPreview();
        return;
      }

      try {
        await this._updatePreviewModel(shape);
      } catch (error) {
        console.error('[CabinetPanelDropControls] Cabinet 3D model is not available:', error);
        reportNonFatalError('sqft-3d-cabinet-drop-model-unavailable', error, {
          dragData,
          shapeId: shape.id,
        });
        this._showUnavailableCabinetModelPopup();
        this._clearPreview();
        return;
      }

      if (!this.previewModel) {
        this._showUnavailableCabinetModelPopup();
        this._clearPreview();
        return;
      }

      this._clearPreview({ keepShape: true });
      if (position) {
        shape.setPosition(position);
        const placement = resolveCabinetPlacement(shape, position, position, getShapeStartBounds(shape));
        if (placement) {
          this._applyPlacementToShape(shape, placement);
        } else {
          shape.setPosition(this._getFreeDropPosition(shape, position).position);
        }
      }
      sqftShapeIntersectionsManager.updateShapesIntersections();
      workspaceControllers.snappedShapeGroups.updateShapesSnappedGroups(false);
      SqftStore.updateCalculations();
      if (this.threeScene.isMobile) {
        store.dispatch(setSQFTActiveShapeId(shape.id));
      }
      await this._reloadCabinets();

      if (this._hasCabinetLoadFailed(shape)) {
        this._removeFailedDroppedShape(shape);
        this._showUnavailableCabinetModelPopup();
        return;
      }

      if (!this._isUpperCabinetDrag(dragData)) {
        await this._reloadCountertops();
      }
    }).catch((error) => {
      console.error('[CabinetPanelDropControls] Could not drop cabinet into 3D scene:', error);
      reportNonFatalError('sqft-3d-cabinet-drop-failed', error, {
        dragData,
      });
      this._showUnavailableCabinetModelPopup();
    });
  }

  _onDragLeave = (event) => {
    if (event.currentTarget === this.domElement) {
      this._clearPreview();
    }
  }
}
